import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Option, useToast, useTranslation } from "@familyzone/component-library";

import FilteringTable from "../FilteringTable";
import { FilteringRule, Keyword, Signature, TimePeriod, AllOptions, lockedOptions } from "../types";
import PolicyModal from "../PolicyModal";
import PolicyLayout from "../PolicyLayout";

import {
  sortPolicies,
  modifyPolicy,
  deletePolicy,
  createPolicy,
  getPolicies,
  getObjects,
  getFingerprints,
  getTimePeriods,
  getSecurityGroups,
  getSignaturesAndKeywords,
} from "../ApiHelpers";
import useIsMounted from "../../../utils/hooks/useIsMounted";
import PageWithHeader from "../../../components/templates/PageWithHeader";
import TestPolicyModal from "../../../components/modals/TestPolicyModal/TestPolicyModal";
import {
  Fingerprint,
  IpObject,
  KeywordObject,
  MacObject,
  WebsiteObject,
  SecurityGroup,
  YoutubeListObject,
} from "../../../modules/criteria/criteriaTypes";
import SignatureSearchModal from "../../../components/modals/SignatureSearchModal";
import { YoutubeDisplayTypeIndex } from "../../../types/Objects";

const FilteringPage: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbs = [
    { title: t("Filtering"), url: "filtering/policies", isActive: false },
    { title: t("Content Filtering"), isActive: true },
  ];

  const title = t("Content Filtering");

  const { errorToast } = useToast();
  const isMounted = useIsMounted();

  const [policyModalOpen, setPolicyModalOpen] = useState<boolean>(false);
  const [testPolicyModalOpen, setTestPolicyModalOpen] = useState<boolean>(false);
  const [editingRule, setEditingRule] = useState<null | FilteringRule>(null);
  const [policyEdit, setPolicyEdit] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [rules, setRules] = useState<FilteringRule[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);

  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [keywordObjects, setKeywordObjects] = useState<KeywordObject[]>([]);
  const [websiteObjects, setWebsiteObjects] = useState<WebsiteObject[]>([]);
  const [ipObjects, setIpObjects] = useState<IpObject[]>([]);
  const [fingerprints, setFingerprints] = useState<Fingerprint[]>([]);
  const [macObjects, setMacObjects] = useState<MacObject[]>([]);
  const [timePeriods, setTimePeriods] = useState<TimePeriod[]>([]);
  const [securityGroups, setSecurityGroups] = useState<SecurityGroup[]>([]);
  const [youtubeListObjects, setYoutubeListObjects] = useState<YoutubeListObject[]>([]);
  const [search, setSearch] = useState<string>("");
  const [signatureSearch, setSignatureSearch] = useState<AllOptions | null>();
  const [lockedFilter, setLockedFilter] = useState<Option | null>(null);

  const handleToggleSearchOpen = () => {
    setSearchOpen(!searchOpen);
  };

  const populateObjects = useCallback(async () => {
    try {
      const objectPromise = getObjects();
      const fingerprintPromise = getFingerprints();
      const timePeriodPromise = getTimePeriods();
      const securityGroupPromise = getSecurityGroups();
      const signatureAndKeywordPromise = getSignaturesAndKeywords();

      setIpObjects((await objectPromise).filter((obj): obj is IpObject => obj.type === 1 || obj.type === 0));
      setFingerprints(await fingerprintPromise);
      setMacObjects((await objectPromise).filter((obj): obj is MacObject => obj.type === 4));
      setTimePeriods(await timePeriodPromise);
      setSecurityGroups(await securityGroupPromise);
      setSignatures(
        (await signatureAndKeywordPromise).signatures
          .filter(
            (s) => s.enabled && !s.hidden && !["sphirewall.application.alwaysallow", "sphirewall.application.alwaysdeny"].includes(s.id)
          )
          .sort((a, b) => (a.name > b.name ? 1 : -1))
      );
      setKeywords((await signatureAndKeywordPromise).keywords);
      setKeywordObjects((await objectPromise).filter((obj): obj is KeywordObject => obj.type === 6));
      setWebsiteObjects((await objectPromise).filter((obj): obj is WebsiteObject => obj.type === 2));
      setYoutubeListObjects((await objectPromise).filter((obj): obj is YoutubeListObject => obj.type === YoutubeDisplayTypeIndex));
    } catch (err) {
      console.error(err);
      errorToast({ title: "Failed to load. Please refresh the page", isClosable: true });
    }
  }, [errorToast]);

  useEffect(() => {
    void populateObjects();
  }, [populateObjects]);

  const handleClosePolicyModal = async (rule: FilteringRule | null) => {
    if (!rule) {
      setPolicyModalOpen(false);
      return;
    } else if (policyEdit === false) {
      await deletePolicy(rule.id);
      if (!isMounted()) return;
      setRules(rules.filter((r) => r.id !== rule.id));
    }
    setPolicyModalOpen(false);
  };

  const handleCloseTestPolicyModal = () => {
    setTestPolicyModalOpen(false);
  };

  const handleClickCreatePolicy = useCallback(async () => {
    try {
      setLoading(true);
      // Add empty rule
      await createPolicy();
      if (!isMounted()) return;
      // Get total policies
      const filteringRules: FilteringRule[] = await getPolicies(false);
      if (!isMounted()) return;
      const newRule = filteringRules[filteringRules.length - 1];
      setRules(filteringRules);
      setEditingRule(newRule);

      setPolicyModalOpen(true);
      setPolicyEdit(false);
    } catch (err) {
      if (!isMounted()) return;
      console.error(err);
      errorToast({
        title: "There was an error creating the policy",
      });
    } finally {
      if (!isMounted()) return;
      setLoading(false);
    }
  }, [isMounted, errorToast]);

  const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleChangeSignatureSearch = (selected: AllOptions | null) => {
    setSignatureSearch(selected);
  };

  const handleClearSearch = () => {
    setSearch("");
  };

  const handleChangedLocked = (selected: Option | null) => {
    setLockedFilter(selected);
  };

  const shownRules = useMemo(() => {
    return rules.filter((rule) => {
      if (!search && !signatureSearch && !lockedFilter) return true;

      const nameMatches = search ? rule.name.toLowerCase().includes(search.toLowerCase()) : true;

      let signatureMatches = true;
      if (signatureSearch && signatureSearch.value) {
        signatureMatches = rule.criteria.some((criteria) => {
          if (criteria.type !== "signature") return false;
          if (!Array.isArray(criteria.conditions)) return false;

          return criteria.conditions.some((conditionId) => {
            if (conditionId === signatureSearch.value) return true;

            const conditionSignature = signatures.find((s) => s.id === conditionId);
            if (!conditionSignature) return false;

            const selectedSignature = signatures.find((s) => s.id === signatureSearch.value);
            if (!selectedSignature) return false;

            if (conditionSignature.category === selectedSignature.id) return true;

            const parentSignature = signatures.find((s) => s.id === conditionSignature.category);
            if (parentSignature && parentSignature.category === selectedSignature.id) return true;

            return false;
          });
        });
      }

      // Check locked/sticky filter
      let lockedMatches = true;
      if (lockedFilter) {
        const isLocked = lockedFilter.value === lockedOptions[0].value;
        const isUnlocked = lockedFilter.value === lockedOptions[1].value;

        lockedMatches = (isLocked && rule.sticky) || (isUnlocked && !rule.sticky);
      }

      return nameMatches && signatureMatches && lockedMatches;
    });
  }, [rules, search, signatureSearch, signatures, lockedFilter]);

  const handleClickTestPolicy = useCallback(() => {
    setTestPolicyModalOpen(true);
  }, []);

  const handleSaveRule = useCallback(
    async (rule: FilteringRule) => {
      try {
        setLoading(true);
        await modifyPolicy(rule);
        if (!isMounted()) return;
        setRules(
          rules.map((r) => {
            if (r.id !== rule.id) return r;
            return rule;
          })
        );
        setPolicyModalOpen(false);
        void populateObjects();
      } catch (err) {
        if (!isMounted()) return;
        console.error(err);
        errorToast({
          title: "There was an error saving the policy",
        });
      } finally {
        if (!isMounted()) return;
        setLoading(false);
      }
    },
    [isMounted, rules, errorToast, populateObjects]
  );

  const handleSortRules = useCallback(
    async (newRules: FilteringRule[]) => {
      try {
        setLoading(true);
        setRules(newRules);
        await sortPolicies(newRules);
      } catch (err) {
        if (!isMounted()) return;
        console.error(err);
        errorToast({
          title: "There was an error sorting the policies",
        });
      } finally {
        if (!isMounted()) return;
        setLoading(false);
      }
    },
    [isMounted, errorToast]
  );

  const handleDeleteRule = useCallback(
    async (rule: FilteringRule) => {
      try {
        if (loading) return;
        setLoading(true);
        await deletePolicy(rule.id);
        if (!isMounted()) return;
        setRules(rules.filter((r) => r.id !== rule.id));
      } catch (err) {
        if (!isMounted()) return;
        console.error(err);
        errorToast({
          title: "There was an error deleting the policy",
        });
      } finally {
        if (!isMounted()) return;
        setLoading(false);
      }
    },
    [isMounted, loading, rules, errorToast]
  );

  const handleEditRule = (rule: FilteringRule) => {
    if (loading) return;
    setSearchOpen(false);
    setEditingRule(rules.find((r) => r.id === rule.id) || rule);
    setPolicyModalOpen(true);
    setPolicyEdit(true);
  };

  const handleEditRuleFromModal = (ruleID: string) => {
    rules.forEach((r) => {
      if (r.id === ruleID) {
        handleEditRule(r);
      }
    });
  };

  const handleCopyRule = useCallback(
    async (rule: FilteringRule) => {
      try {
        if (loading) return;
        setLoading(true);
        await createPolicy();
        if (!isMounted()) return;
        const filteringRules: FilteringRule[] = await getPolicies(false);
        if (!isMounted()) return;
        setRules(filteringRules);
        const newRule = filteringRules[filteringRules.length - 1];
        const newRuleRecord = newRule as unknown as Record<string, unknown>;
        const ruleRecord = rule as unknown as Record<string, unknown>;
        for (const key in ruleRecord) {
          if (key === "id") continue;
          if (Object.prototype.hasOwnProperty.call(ruleRecord, key)) {
            newRuleRecord[key] = ruleRecord[key];
          }
        }
        newRule.name = `copy of - ${rule.name}`;
        newRule.enabled = false;
        setRules(filteringRules);
        setEditingRule(newRule);
        setPolicyModalOpen(true);
        setPolicyEdit(false);
      } catch (err) {
        console.error(err);
      } finally {
        if (!isMounted()) return;
        setLoading(false);
      }
    },
    [loading, isMounted]
  );

  const handleToggleRule = useCallback(
    async (rule: FilteringRule) => {
      try {
        if (loading) return;
        const newRule = rule;
        newRule.enabled = !newRule.enabled;

        setLoading(true);
        await modifyPolicy(newRule);
        if (!isMounted()) return;
        setRules(
          rules.map((r) => {
            if (r.id !== rule.id) return r;
            return newRule;
          })
        );
      } catch (err) {
        if (!isMounted()) return;
        console.error(err);
        errorToast({
          title: "There was an error modifying the policy",
        });
      } finally {
        if (!isMounted()) return;
        setLoading(false);
      }
    },
    [isMounted, loading, rules, errorToast]
  );

  const populateRules = useCallback(async () => {
    try {
      setLoading(true);
      // Remove any content mod rules from the ruleset before showing them
      const filteringRules: FilteringRule[] = await getPolicies(false);
      if (!isMounted()) return;
      setRules(filteringRules);
    } catch (err) {
      if (!isMounted()) return;
      console.error(err);
      errorToast({
        title: "There was an error populating the policies",
      });
    } finally {
      if (!isMounted()) return;
      setLoading(false);
    }
  }, [isMounted, errorToast]);

  useEffect(() => {
    populateRules().catch(() => "");
  }, [populateRules]);

  const handleRefresh = () => {
    populateRules().catch(() => "");
  };

  return (
    <PageWithHeader breadcrumbs={breadcrumbs} title={title}>
      <Box width="100%" mt="sp16">
        <PolicyLayout
          loading={loading && rules.length === 0}
          disabled={loading && rules.length !== 0}
          showTestPolicy
          showCategorySearch
          showObjectLists
          onChangeLocked={handleChangedLocked}
          search={search}
          signatures={signatures}
          showFilter={true}
          onChangeSearch={handleChangeSearch}
          onClearSearch={handleClearSearch}
          onChangeSignatureSearch={(e: AllOptions | null) => void handleChangeSignatureSearch(e)}
          onClickCreatePolicy={() => void handleClickCreatePolicy().catch(() => "")}
          onClickTestPolicy={() => void handleClickTestPolicy()}
          onClickURLSearch={() => void handleToggleSearchOpen()}
        >
          <FilteringTable
            disableDrag={search !== "" || !!signatureSearch}
            filteredRules={shownRules}
            searchFilter={search !== "" || !!signatureSearch}
            disabled={loading && rules.length !== 0}
            loading={loading && rules.length === 0}
            rules={rules}
            onSortRules={(rules: FilteringRule[]) => void handleSortRules(rules)}
            onTempSortRules={(rules: FilteringRule[]) => setRules(rules)}
            onToggleRule={(rule: FilteringRule) => void handleToggleRule(rule)}
            onDeleteRule={(rule: FilteringRule) => void handleDeleteRule(rule)}
            onEditRule={(rule: FilteringRule) => void handleEditRule(rule)}
            onCopyRule={(rule: FilteringRule) => void handleCopyRule(rule)}
          />
        </PolicyLayout>
        <PolicyModal
          loading={loading}
          open={policyModalOpen}
          rule={editingRule}
          editing={policyEdit}
          onClose={(rule: FilteringRule | null) => void handleClosePolicyModal(rule)}
          onSubmit={(rule: FilteringRule) => void handleSaveRule(rule)}
          signatures={signatures}
          keywords={keywords}
          keywordObjects={keywordObjects}
          websiteObjects={websiteObjects}
          ipObjects={ipObjects}
          macObjects={macObjects}
          securityGroups={securityGroups}
          fingerprints={fingerprints}
          timePeriods={timePeriods}
          youtubeListObjects={youtubeListObjects}
        />
        <TestPolicyModal
          signatures={signatures}
          keywords={keywords}
          keywordObjects={keywordObjects}
          youtubeListObjects={youtubeListObjects}
          websiteObjects={websiteObjects}
          open={testPolicyModalOpen}
          onClose={handleCloseTestPolicyModal}
          editRule={handleEditRuleFromModal}
          onRefresh={handleRefresh}
          rules={rules}
        />
        <SignatureSearchModal
          data-testid="search-modal"
          open={searchOpen}
          signatures={signatures}
          onClose={handleToggleSearchOpen}
          onSignatureClick={handleEditRule}
          onRefresh={() => void populateRules()}
        />
      </Box>
    </PageWithHeader>
  );
};

export default FilteringPage;
